// tslint:disable:no-commented-code
// tslint:disable:no-duplicate-string
// tslint:disable:only-arrow-functions

import { recommendationsPanelsLocaleKeys } from '@epicuro-next/constants/features/localization';
import { IProductReference } from '@epicuro-next/data/product';
import {
  EventHandlerPayloadResolveMoment,
  IDynamicBindableValue,
  IDynamicItem,
  IDynamicItemValue,
  IDynamicSimpleText,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { clearBasketNotification } from '@epicuro-next/state/basket-state';
import { Product, SearchQuery } from 'graphql-api/gql-api-interfaces';

import { eventHandlerFromAction } from '../../../utils/eventhandler-from-action';
import { createLink } from '../../components/link';
import {
  configStripSetProductSource,
  openConfigStripEventHandler,
  trackListAction,
} from '../../events';
import { hasError, isLoading } from '../../loading-state-wrapper-helpers';
import { hasData, hasNoItems, productData } from '../../product-data';

import {
  backgroundMedia,
  errorMessageItem,
  getIsDoneLoadingWrapper,
  getIsLoadingWrapper,
  getItemMediaPreview,
  getItemsArrayRenderer,
  getSearchBarPanelSubTitle,
  getSearchBarPanelTitle,
  noItemsItem,
} from './search-bar-helper';

export const panelName = 'SearchBarBasePanel';
const fontSize = '0.75rem';

const panelNameProvider: IDynamicBindableValue = {
  provider: 'id:search-bar-base-root',
  property: 'panelName',
};
const panelStyleProvider: IDynamicBindableValue = {
  provider: 'id:search-bar-base-root',
  property: 'panelTrackingId',
};
const listIdProvider: IDynamicBindableValue = {
  provider: 'id:search-bar-base-root',
  property: 'listId',
};

const brandIdProvider = {
  provider: 'GlobalStoreProviderService',
  property: {
    paths: 'sites.currentSite.brandId',
  },
};

const siteIdProvider = {
  provider: 'GlobalStoreProviderService',
  property: {
    paths: 'sites.currentSite.siteId',
  },
};

const accountIdProvider = {
  provider: 'GlobalStoreProviderService',
  property: {
    paths: 'session.accountId',
  },
};

const isLoadingWrapper = getIsLoadingWrapper();

const itemMediaPreview = getItemMediaPreview(
  panelNameProvider,
  panelStyleProvider,
  {
    classes: ['marketing-panel__item'],
    style: {
      'border-radius': 'var(--border-radius-lg, .35rem)',
      margin: '0',
      'max-height': '165px',
    },
  },
  [
    openConfigStripEventHandler('self'),
    eventHandlerFromAction(clearBasketNotification),
    configStripSetProductSource({
      listIdProvider,
      panelNameProvider,
      panelTrackingIdProvider: panelStyleProvider,
    }),
  ],
);

const itemsArrayRenderer = getItemsArrayRenderer(itemMediaPreview);

const listName = getSearchBarPanelTitle('list-name-content', {
  provider: 'parent',
  property: 'listName',
});

const productName = getSearchBarPanelSubTitle('product-name-content', {
  provider: 'parent',
  property: 'productData',
  transform: function (data: { product: Product }[]) {
    return data[0]?.product?.articleTitle;
  }.toString(),
});

const seeMoreLinkContent: IDynamicSimpleText = {
  id: 'search-panel-see-more-elements-link-text`',
  contentTypeId: 'dynamic-elements-simple-text',
  styles: {
    style: {
      'font-size': '14px',
      'font-weight': '600',
      padding: '0',
    },
    classes: ['text-primary', 'col'],
    variables: {
      '--text-dynamic-color': '#3f51b5',
      '--color-brand-primary': '#3f51b5',
    },
  },
  inputs: {
    text: recommendationsPanelsLocaleKeys.panelSeeMoreButton,
  },
};

const seeMoreLink = createLink(
  'search-panel-see-more-elements-link',
  {
    provider: 'id:search-bar-base-root',
    property: 'listLink',
  },
  seeMoreLinkContent,
);

seeMoreLink.eventHandlers = {
  onClick: [
    trackListAction({
      listIdProvider,
    }),
    configStripSetProductSource(
      {
        listIdProvider,
        panelNameProvider,
        panelTrackingIdProvider: panelStyleProvider,
      },
      {
        seeMore: true,
        payloadFetchMoment:
          EventHandlerPayloadResolveMoment.BeforeAddEventListener,
      },
    ),
  ],
};

seeMoreLink.styles.style = {
  display: 'flex',
  'justify-content': 'end',
  'margin-top': '3px',
};
seeMoreLink.styles.classes = ['search-bar-see-more-link-container'];
seeMoreLink.styles.css =
  '.search-bar-see-more-link-container a { padding: 0 0.5rem; }';

const doneLoadingWrapperChildren = [
  listName,
  productName,
  backgroundMedia,
  itemsArrayRenderer,
  errorMessageItem,
  noItemsItem,
  seeMoreLink,
];

const isDoneLoadingWrapper = getIsDoneLoadingWrapper(
  doneLoadingWrapperChildren,
);

export function getAvailableProductsReferences(): IDynamicBindableValue {
  return {
    provider: 'HttpBodyProvider',
    property: {
      values: [getProductReferences(), siteIdProvider, accountIdProvider],
      transform: function ([productReferences, siteId, accountId]: [
        IProductReference[],
        string,
        string,
      ]) {
        const body = {
          siteId,
          productReferences,
        };

        const url = `/api/products/${accountId}/check-availability`;
        const method = 'POST';
        const fetchString =
          window.neoclesDynamicFramework.recommendationsPanels.methods.createHttpRequestFetchString(
            url,
            method,
            body,
          );

        return fetchString;
      }.toString(),
    },
    transform: function (data: {
      availableProductReferences: IProductReference[];
    }) {
      return data.availableProductReferences;
    }.toString(),
  };
}

export function getProductReferences(): IDynamicBindableValue {
  return {
    provider: 'HttpBodyProvider',
    property: {
      values: [listIdProvider],
      transform: function (listId: string) {
        return `/api/v2/engagement/productlist/${listId}`;
      }.toString(),
    },
    transform: function (data: string) {
      return JSON.parse(data)?.productsReference;
    }.toString(),
  };
}

export function getListName(): IDynamicBindableValue {
  return {
    provider: 'HttpBodyProvider',
    property: {
      values: [listIdProvider],
      transform: function (listId: string) {
        return `/api/v2/engagement/productlist/${listId}`;
      }.toString(),
    },
    transform: function (data: string) {
      return JSON.parse(data)?.name;
    }.toString(),
  };
}

export function stateWrapper(): IDynamicItemValue {
  return {
    provider: 'ProductBasketProvider',
    property: {
      values: [
        getAvailableProductsReferences(),
        brandIdProvider,
        siteIdProvider,
      ],
      transform: function ([productReferences, brandId, siteId]: [
        IProductReference[],
        string,
        string,
      ]): SearchQuery {
        const target = brandId ?? siteId;

        const products = productReferences?.filter(
          (product: IProductReference) =>
            product.brandId?.toLowerCase() === target.toLowerCase(),
        );

        if (!products.length) {
          return { q: '' };
        }

        const randomIndex = Math.floor(Math.random() * products.length);
        const productId = !products.length
          ? productReferences?.[0]?.productId
          : products[randomIndex].productId;

        if (productId.startsWith('PUM-')) {
          return { q: productId.replace('PUM-', '') };
        }

        return { q: productId };
      }.toString(),
    },
    wrapWithLoadingState: true,
    transform: function (data: { products: Product[] }) {
      const product = data.products[0];

      if (!product) {
        return;
      }

      const complexObject = {
        media: {
          url: product.mainImageUrl,
          mediaType: 'image',
        },
        configStrip: {
          overlayPath: [
            'cs',
            product.brandId,
            product.parentId,
            product.productId,
          ],
        },
        product,
      };

      return {
        products: [complexObject],
      };
    }.toString(),
  };
}

export const searchBarBasePanel: IDynamicItem = {
  id: 'search-bar-base-root',
  contentTypeId: 'dynamic-collections-renderer',
  data: {
    panelName,
    listId: 'e2623b68-5a0e-4bb5-aa1a-9834a680ce72',
    loadingStateWrapper: stateWrapper(),
    isLoading: isLoading(),
    listName: getListName(),
    productData: productData(),
    hasError: hasError(),
    hasData: hasData('self'),
    hasNoItems: hasNoItems(),
    panelTrackingId: 'SQMSD1',
    showProductHeader: true,
    listLink: '',
    displayLink: true,
    displayLinkText: recommendationsPanelsLocaleKeys.panelSeeMoreButton,
    displayLinkStyle: `width: 100%; font-size: ${fontSize}; text-align: end; padding: 3px 0;`,
  },
  children: [
    {
      id: 'row-wrapper',
      contentTypeId: 'dynamic-collections-renderer',
      children: [
        isLoadingWrapper,
        {
          id: 'col-wrapper',
          contentTypeId: 'dynamic-collections-renderer',
          children: [isDoneLoadingWrapper],
        },
      ],
    },
  ],
};
